import React from "react";
import {
  Activity,
  Award,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Compass,
  History,
  SearchCheck,
} from "lucide-react";
import {
  changeResponseLabel,
  responseMagnitude,
} from "@/components/daily/adaptiveScore";
import { AppCard, AppPageHeader, StatCard } from "@/components/daily/CompanionAppUi";

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const responseLabel = (entry) => {
  if (!entry.completed) return "Not completed";
  if (entry.feltWorse || entry.response === "worse") return "Changed in wrong direction";
  const magnitude = responseMagnitude(entry);
  if (magnitude) return changeResponseLabel(magnitude);
  return "Completed — response not rated";
};

const patternLabel = (classification) =>
  ({
    "larger-change-pattern": "Larger change reported repeatedly",
    "needs-adjustment": "Needs adjustment",
    "often-little-change": "Often little change",
    "context-dependent": "Depends on context",
  })[classification] || "Depends on context";

const CompanionProgressPageV2 = ({ progress, summary, entries, onNavigate }) => (
  <div data-testid="companion-progress-page">
    <AppPageHeader
      eyebrow="Progress"
      title="Your patterns, participation, and response history"
      body="APOS tracks what you report and what it learns from comparable before-to-after responses. Missing a day does not subtract progress, and completion by itself is not treated as nervous-system adaptation."
    />

    <AppCard tone="action">
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white">
          <SearchCheck size={23} className="forr-text-teal-deep" />
        </div>
        <div>
          <div className="forr-overline">APOS learning</div>
          <h2 className="mt-2 font-display text-3xl forr-text-ink">
            {progress.observationsCount} response observation{progress.observationsCount === 1 ? "" : "s"}
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed forr-text-soft">
            The goal is not to raise one universal score. APOS uses repeated observations to learn which inputs, doses, and contexts are more or less useful for you.
          </p>
        </div>
      </div>
    </AppCard>

    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatCard icon={CalendarDays} value={progress.checkInCount} label="Check-ins" />
      <StatCard icon={CheckCircle2} value={progress.completedCount} label="Completed inputs" />
      <StatCard icon={SearchCheck} value={progress.observationsCount} label="Response observations" />
      <StatCard icon={Compass} value={progress.patternsIdentified} label="Patterns identified" />
    </div>

    <div className="mt-4 grid gap-4 md:grid-cols-[1.2fr_0.8fr]">
      <AppCard tone="information">
        <div className="forr-overline">What APOS is learning</div>
        <h2 className="mt-2 font-display text-2xl forr-text-ink">Comparable response patterns</h2>
        {progress.patterns.length ? (
          <div className="mt-4 space-y-3">
            {progress.patterns.slice(0, 8).map((pattern) => (
              <div key={pattern.key} className="rounded-2xl p-4 forr-surface-tint">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium forr-text-ink">{pattern.title}</div>
                    <div className="mt-1 text-xs forr-text-mute">{pattern.state}</div>
                  </div>
                  <span className="rounded-full px-2.5 py-1 text-[9px] forr-text-teal-deep" style={{ backgroundColor: "var(--forr-teal-tint)" }}>
                    {pattern.count} comparable
                  </span>
                </div>
                <p className="mt-3 text-xs leading-relaxed forr-text-soft">
                  {patternLabel(pattern.classification)}. Large/significant change: {pattern.significantOrLarge}; no change: {pattern.noChange}; wrong direction: {pattern.worse}.
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-4 text-sm leading-relaxed forr-text-soft">
            APOS needs repeated observations in comparable situations before it labels a personal pattern. Keep checking in and recording what actually changed.
          </p>
        )}
      </AppCard>

      <AppCard tone="information">
        <div className="forr-overline">Response magnitudes</div>
        <h2 className="mt-2 font-display text-2xl forr-text-ink">What changed afterward</h2>
        <div className="mt-4 space-y-2">
          {progress.responseCounts.map((option) => (
            <div key={option.value} className="flex items-center justify-between gap-3 rounded-xl p-3 forr-surface-tint">
              <span className="text-xs font-medium forr-text-ink">{option.label}</span>
              <span className="font-display text-xl forr-text-teal-deep">{option.count}</span>
            </div>
          ))}
          {progress.worseCount > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-xl p-3" style={{ backgroundColor: "#f8e5e2" }}>
              <span className="text-xs font-medium forr-text-ink">Changed in wrong direction</span>
              <span className="font-display text-xl" style={{ color: "#a54242" }}>{progress.worseCount}</span>
            </div>
          )}
        </div>
      </AppCard>
    </div>

    <AppCard tone="information" className="mt-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="forr-overline">Permanent milestones</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">Participation and learning rewards</h2>
          <p className="mt-2 text-xs leading-relaxed forr-text-mute">
            Milestones recognize showing up, completing experiments, and recording observations. They never decrease because you missed a day or reported a difficult nervous-system state.
          </p>
        </div>
        <Award size={23} className="forr-text-teal-deep" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
        {summary.badges.map((badge) => (
          <div
            key={badge.key}
            className="rounded-2xl p-3 text-center"
            style={{
              backgroundColor: badge.earned ? "var(--forr-teal-tint)" : "var(--forr-canvas-alt)",
              opacity: badge.earned ? 1 : 0.45,
            }}
          >
            <div className="text-2xl" aria-hidden="true">{badge.emoji}</div>
            <div className="mt-1 text-[9px] leading-tight forr-text-mute">{badge.label}</div>
          </div>
        ))}
      </div>
    </AppCard>

    <section className="mt-7">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="forr-overline">History</div>
          <h2 className="mt-2 font-display text-2xl forr-text-ink">Recent check-ins and outputs</h2>
        </div>
        <History size={22} className="forr-text-teal-deep" />
      </div>

      {entries.length ? (
        <div className="mt-4 space-y-3">
          {entries.slice(0, 20).map((entry) => (
            <article key={entry.id} className="rounded-[22px] p-5 forr-surface" style={{ border: "1px solid var(--forr-line-soft)" }}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] forr-text-mute">{formatDateTime(entry.createdAt)}</div>
                  <h3 className="mt-2 font-display text-lg forr-text-ink">{entry.system || "Reported response"}</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {(entry.reportedStates || []).map((label) => (
                      <span key={label} className="rounded-full px-2.5 py-1 text-[10px] forr-text-teal-deep" style={{ backgroundColor: "var(--forr-teal-tint)" }}>
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="shrink-0 rounded-full px-3 py-1.5 text-[10px] font-medium forr-surface-tint forr-text-ink">
                  {responseLabel(entry)}
                </div>
              </div>
              <div className="mt-4 rounded-2xl p-4 forr-surface-tint">
                <div className="text-[10px] uppercase tracking-[0.16em] forr-text-mute">Matched output</div>
                <div className="mt-1 font-medium forr-text-ink">{entry.recommendation?.title || "Physiological input"}</div>
                {entry.recommendation?.duration && (
                  <div className="mt-1 text-xs forr-text-teal-deep">{entry.recommendation.duration}</div>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <AppCard className="mt-4">
          <div className="flex items-start gap-4">
            <Activity size={22} className="mt-0.5 forr-text-teal-deep" />
            <div>
              <h3 className="font-display text-xl forr-text-ink">No response history yet</h3>
              <p className="mt-2 text-sm leading-relaxed forr-text-soft">
                Complete a current nervous-system check-in, use the matched input, and record what changed afterward.
              </p>
              <button type="button" onClick={() => onNavigate("checkIn")} className="mt-4 inline-flex items-center gap-2 text-sm font-medium forr-text-teal-deep">
                Start first check-in <ChevronRight size={15} />
              </button>
            </div>
          </div>
        </AppCard>
      )}
    </section>
  </div>
);

export default CompanionProgressPageV2;
